/**
 * Pure geometry helpers behind unit boundary editing on the map: per-piece
 * vertex/midpoint derivation, the move/insert/delete operations, and vertex
 * snapping.
 *
 * Ported from the Unit Builder mock (mock-ui
 * `inventory-management-type-curve/v2/components/EditSlotMap.tsx` and
 * `data/landgridIntersect.ts`). The mock is Leaflet and stores coordinates
 * `[lat, lng]`; everything here is Mapbox/GeoJSON `[lng, lat]`.
 *
 * A unit boundary is a list of disjoint `Piece`s rather than a single ring:
 * composing a unit from several survey areas can produce a shape that is not
 * contiguous, and each piece is edited independently.
 */

/** A single closed (or open) boundary ring, `[lng, lat]` positions. */
export type Piece = GeoJSON.Position[];

/** A unit boundary: one or more disjoint pieces. */
export type Pieces = Piece[];

/** Below this a piece stops being a polygon, so deletes are refused. */
export const MIN_PIECE_VERTICES = 3;

/** Snap radius from a dragged vertex to the nearest candidate, in feet. */
export const SNAP_RADIUS_FT = 300;

const FT_PER_METER = 3.28084;

/** `SNAP_RADIUS_FT` expressed in metres, for {@link snapToNearestVertex}. */
export const SNAP_RADIUS_M = SNAP_RADIUS_FT / FT_PER_METER;

/**
 * Whether a piece is a closed ring: its last coordinate repeats its first.
 * @param {Piece} piece - The piece.
 * @return {boolean} True when closed.
 */
const isClosed = (piece: Piece): boolean =>
  piece.length > 1 &&
  piece[0][0] === piece[piece.length - 1][0] &&
  piece[0][1] === piece[piece.length - 1][1];

/**
 * Distinct vertices per piece — the closing coordinate of a closed ring is
 * dropped so it doesn't render a second handle on top of the first.
 * @param {Pieces} pieces - Boundary pieces.
 * @return {Pieces} Per-piece vertices with no duplicate closing coordinate.
 */
export function pieceVertices(pieces: Pieces): Pieces {
  return pieces.map((piece) => {
    return isClosed(piece) ? piece.slice(0, -1) : piece.slice();
  });
}

/**
 * Edge midpoints per piece. Index `i` is the midpoint of the edge leaving
 * vertex `i`, including the wrap-around edge from the last vertex back to the
 * first — so a closed ring gets a handle on every edge.
 * @param {Pieces} vertices - Output of {@link pieceVertices}.
 * @return {Pieces} Per-piece edge midpoints.
 */
export function pieceMidpoints(vertices: Pieces): Pieces {
  return vertices.map((verts) => {
    const m = verts.length;
    if (m < 2) return [];
    return verts.map((a, i) => {
      const b = verts[(i + 1) % m];
      return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    });
  });
}

/**
 * Apply `mutate` to one piece's vertices, preserving whether that piece was
 * closed. Returns `null` when the piece doesn't exist or `mutate` declines,
 * so callers can distinguish "no change" from "changed".
 * @param {Pieces} pieces - Current boundary.
 * @param {number} p - Index of the piece to edit.
 * @param {Function} mutate - Vertex transform; return `null` to decline.
 * @return {Pieces | null} New boundary, or `null` if nothing changed.
 */
export function editPiece(
  pieces: Pieces,
  p: number,
  mutate: (verts: Piece) => Piece | null
): Pieces | null {
  const piece = pieces[p];
  if (!piece) return null;

  const closed = isClosed(piece);
  const verts = closed ? piece.slice(0, -1) : piece.slice();
  const next = mutate(verts);
  if (!next) return null;

  const out = pieces.map((r) => r.slice());
  out[p] = closed ? [...next, next[0]] : next;
  return out;
}

/**
 * Move vertex `i` of piece `p` to `next`.
 * @param {Pieces} pieces - Current boundary.
 * @param {number} p - Piece index.
 * @param {number} i - Vertex index.
 * @param {GeoJSON.Position} next - New `[lng, lat]` position.
 * @return {Pieces | null} New boundary, or `null` if nothing changed.
 */
export function moveVertex(
  pieces: Pieces,
  p: number,
  i: number,
  next: GeoJSON.Position
): Pieces | null {
  return editPiece(pieces, p, (verts) => {
    if (i < 0 || i >= verts.length) return null;
    return verts.map((v, idx) => (idx === i ? next : v));
  });
}

/**
 * Insert a vertex immediately after vertex `i` of piece `p` — the operation
 * behind clicking an edge midpoint.
 * @param {Pieces} pieces - Current boundary.
 * @param {number} p - Piece index.
 * @param {number} i - Index of the vertex the new one follows.
 * @param {GeoJSON.Position} point - Position to insert.
 * @return {Pieces | null} New boundary, or `null` if nothing changed.
 */
export function insertVertex(
  pieces: Pieces,
  p: number,
  i: number,
  point: GeoJSON.Position
): Pieces | null {
  return editPiece(pieces, p, (verts) => {
    if (i < 0 || i >= verts.length) return null;
    const out = verts.slice();
    out.splice(i + 1, 0, point);
    return out;
  });
}

/**
 * Delete vertex `i` of piece `p`. Refused (returns `null`) when the piece is
 * already down to a triangle.
 * @param {Pieces} pieces - Current boundary.
 * @param {number} p - Piece index.
 * @param {number} i - Vertex index.
 * @return {Pieces | null} New boundary, or `null` if the delete was refused.
 */
export function deleteVertex(
  pieces: Pieces,
  p: number,
  i: number
): Pieces | null {
  return editPiece(pieces, p, (verts) => {
    if (verts.length <= MIN_PIECE_VERTICES) return null;
    if (i < 0 || i >= verts.length) return null;
    const out = verts.slice();
    out.splice(i, 1);
    return out;
  });
}

/**
 * A vertex is compared at this fraction of its true distance, so a corner wins
 * over an edge when it is genuinely close.
 *
 * Without it the snap radius works against itself: at 300 ft a corner 250 ft
 * away would beat the edge 10 ft under the cursor, and dragging along a boundary
 * would keep jumping to its ends.
 */
const VERTEX_BIAS = 0.5;

/**
 * The point on segment `a`→`b` closest to `p`, clamped to the segment.
 *
 * Clamping is what stops a projection landing past a corner, out where the edge
 * does not actually run. Worked in the same normalised space as the scoring, so
 * "closest" means the same thing here as it does there.
 *
 * @param {Array} p - The point, in normalised units.
 * @param {Array} a - Segment start, in normalised units.
 * @param {Array} b - Segment end, in normalised units.
 * @return {number} How far along the segment the closest point lies, 0 to 1.
 */
const projectOnSegment = (
  p: [number, number],
  a: [number, number],
  b: [number, number]
): number => {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const lengthSq = abx * abx + aby * aby;
  if (lengthSq === 0) return 0;
  const t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / lengthSq;
  return Math.min(1, Math.max(0, t));
};

/**
 * Snap a point onto the rings' corners AND edges, or onto one of the loose
 * points, whichever is nearest within `maxMeters`.
 *
 * Corners alone are not enough to follow a boundary: most of a development
 * area's outline is edge, so dragging a vertex against the middle of one used to
 * snap to nothing and land wherever the cursor was. Projecting onto the segments
 * is what lets the editable boundary sit ON the unit rather than beside it.
 *
 * Scoring is the same locally normalised space `snapToNearestVertex` uses — a
 * degree of longitude shrinks with latitude, so the threshold is an ellipse
 * (`dx² + dy² <= 1` after dividing each axis by its own degree budget), not a
 * circle — with corners biased by {@link VERTEX_BIAS}.
 *
 * @param {GeoJSON.Position} point - Dragged `[lng, lat]` position.
 * @param {Array} rings - Rings whose corners and edges may be snapped to.
 * @param {GeoJSON.Position[]} points - Loose positions with no edges (grid dots).
 * @param {number} maxMeters - Snap radius in metres.
 * @return {GeoJSON.Position} The snapped position, or `point` unchanged.
 */
export function snapToRings(
  point: GeoJSON.Position,
  rings: GeoJSON.Position[][],
  points: GeoJSON.Position[] = [],
  maxMeters: number = SNAP_RADIUS_M
): GeoJSON.Position {
  const [lon, lat] = point;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const dLatDeg = maxMeters / 111320;
  const dLonDeg = maxMeters / (111320 * (cosLat || 1e-6));

  // Everything is scored in units of the snap radius, so "within range" is
  // score <= 1 whichever kind of candidate it came from.
  /**
   * A position's offset from the cursor, in snap radii.
   * @param {GeoJSON.Position} position - The position.
   * @return {[number, number]} The offset, east and north.
   */
  const norm = (position: GeoJSON.Position): [number, number] => [
    (position[0] - lon) / dLonDeg,
    (position[1] - lat) / dLatDeg,
  ];

  let best: GeoJSON.Position | null = null;
  let bestScore = Infinity;

  /**
   * Keeps a vertex as the best snap target when it is in range and nearer than
   * the best so far, with vertices favoured over edges.
   * @param {GeoJSON.Position} position - The vertex.
   */
  const considerVertex = (position: GeoJSON.Position) => {
    const [dx, dy] = norm(position);
    const score = dx * dx + dy * dy;
    if (score > 1) return;
    const biased = score * VERTEX_BIAS * VERTEX_BIAS;
    if (biased < bestScore) {
      bestScore = biased;
      best = position;
    }
  };

  for (const position of points) considerVertex(position);

  for (const ring of rings) {
    for (let i = 0; i < ring.length; i += 1) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      considerVertex(a);

      const na = norm(a);
      const nb = norm(b);
      const t = projectOnSegment([0, 0], na, nb);
      const dx = na[0] + (nb[0] - na[0]) * t;
      const dy = na[1] + (nb[1] - na[1]) * t;
      const score = dx * dx + dy * dy;
      if (score > 1 || score >= bestScore) continue;
      bestScore = score;
      best = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
  }

  return best ?? point;
}

/**
 * Snap a point to the nearest candidate within `maxMeters`, or return it
 * unchanged when nothing is close enough.
 *
 * Distance is scored in a locally normalised space rather than in raw degrees:
 * a degree of longitude shrinks with latitude, so the threshold is an ellipse
 * (`dx² + dy² <= 1` after dividing each axis by its own degree budget), not a
 * circle. Candidates are scanned linearly — callers are expected to pass only
 * the vertices near the drag, not every landgrid vertex on screen.
 *
 * @param {GeoJSON.Position} point - Dragged `[lng, lat]` position.
 * @param {GeoJSON.Position[]} candidates - Positions to snap to.
 * @param {number} maxMeters - Snap radius in metres.
 * @return {GeoJSON.Position} The snapped position, or `point` unchanged.
 */
export function snapToNearestVertex(
  point: GeoJSON.Position,
  candidates: GeoJSON.Position[],
  maxMeters: number = SNAP_RADIUS_M
): GeoJSON.Position {
  const [lon, lat] = point;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const dLatDeg = maxMeters / 111320;
  const dLonDeg = maxMeters / (111320 * (cosLat || 1e-6));

  let best: GeoJSON.Position | null = null;
  let bestScore = Infinity;

  for (const candidate of candidates) {
    const dx = (candidate[0] - lon) / dLonDeg;
    const dy = (candidate[1] - lat) / dLatDeg;
    const score = dx * dx + dy * dy;
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best && bestScore <= 1 ? best : point;
}

/**
 * Convert boundary pieces to a GeoJSON geometry for rendering. Each piece is
 * closed if it isn't already; pieces with fewer than three vertices are
 * dropped rather than emitted as a degenerate polygon.
 * @param {Pieces} pieces - Boundary pieces.
 * @return {GeoJSON.MultiPolygon} One polygon per piece.
 */
export function piecesToMultiPolygon(pieces: Pieces): GeoJSON.MultiPolygon {
  const coordinates = pieces
    .filter((piece) => pieceVertices([piece])[0].length >= MIN_PIECE_VERTICES)
    .map((piece) => [isClosed(piece) ? piece : [...piece, piece[0]]]);

  return { type: 'MultiPolygon', coordinates };
}
